import {Icon, Show, Text, type ThemeColorTextPath} from 'platform/foundation';
import styled from 'styled-components';
import {match} from 'ts-pattern';

import i18n from '@dms/i18n';

import {suffixTestId, type TestIdProps} from 'shared/web';

export type ConfiguratorStep = {
  title: string;
  isDisabled?: boolean;
  isComplete?: boolean;
};

export type ConfiguratorStepperProps = TestIdProps & {
  steps: ConfiguratorStep[];
  currentIndex: number;
  onStepChange?: (index: number) => void;
  label?: string;
};

const TRANSITION = '160ms cubic-bezier(0.25, 1, 0.5, 1)';

const List = styled.ol`
  display: flex;
  align-items: flex-start;
  width: 100%;
  list-style: none;
  padding: 0;
`;

const Item = styled.li`
  position: relative;
  display: flex;
  flex: 1 1 0;
  min-width: 0;

  /* Edge steps take half a slice, so the first and last bullet sit on the container edges
     instead of being inset by half a slice — the row then reads as full width. */
  &:first-child,
  &:last-child {
    flex-grow: 0.5;
  }
`;

// eslint-disable-next-line eag/no-css-property -- Muted pre-existing violation
const StepTitle = styled.span`
  display: inline-flex;
  justify-content: center;
  width: 100%;
  min-width: 0;
  text-align: center;

  [data-align='start'] > & {
    justify-content: flex-start;
    text-align: left;
  }

  [data-align='end'] > & {
    justify-content: flex-end;
    text-align: right;
  }

  /* Compact layouts keep only the current label; other steps remain named by their buttons. */
  @media ${({theme}) => theme.devices.tablet}, ${({theme}) => theme.devices.mobile} {
    &[data-current='false'] {
      display: none;
    }
  }
`;

const StepButton = styled.button`
  position: relative;
  z-index: 1;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  min-width: 0;
  gap: ${({theme}) => theme.getSize(2)};

  &[data-align='start'] {
    align-items: flex-start;
  }

  &[data-align='end'] {
    align-items: flex-end;
  }

  padding: ${({theme}) => theme.getSize(2)};
  border: 0;
  border-radius: ${({theme}) => theme.radii.large};
  background: transparent;
  cursor: pointer;
  user-select: none;
  transition:
    background-color ${TRANSITION},
    transform ${TRANSITION};

  &:not(:disabled):active {
    transform: scale(0.97);
  }

  @media (prefers-reduced-motion: reduce) {
    &:not(:disabled):active {
      transform: none;
    }
  }

  /* Touch taps leave a sticky hover behind without this. */
  @media (hover: hover) and (pointer: fine) {
    &:not(:disabled):hover {
      background-color: ${({theme}) => theme.colors.palettes.neutral[20][100]};
    }
  }

  &:disabled {
    cursor: not-allowed;
  }

  &[data-readonly='true']:disabled {
    cursor: default;
  }

  &:focus-visible {
    outline: ${({theme}) => `${theme.outline.default} ${theme.colors.general.accent}`};
    outline-offset: ${({theme}) => theme.outline.offset};
  }
`;

const Bullet = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${({theme}) => theme.getSize(6)};
  height: ${({theme}) => theme.getSize(6)};
  border-radius: ${({theme}) => theme.radii.circular};
  border: 1px solid ${({theme}) => theme.colors.palettes.neutral[50][100]};
  background-color: ${({theme}) => theme.colors.general.white};
  color: ${({theme}) => theme.colors.text.secondary};
  transition:
    background-color ${TRANSITION},
    border-color ${TRANSITION},
    color ${TRANSITION};

  &[data-state='current'] {
    border-color: ${({theme}) => theme.colors.general.accent};
    background-color: ${({theme}) => theme.colors.general.accent};
    color: ${({theme}) => theme.colors.general.white};
    box-shadow: 0 0 0 ${({theme}) => theme.getSize(1)}
      ${({theme}) => theme.colors.palettes.blue[20][100]};
  }

  &[data-state='done'] {
    border-color: ${({theme}) => theme.colors.general.accent};
    background-color: ${({theme}) => theme.colors.general.accent};
    color: ${({theme}) => theme.colors.general.white};
  }

  &[data-state='attention'] {
    border-color: ${({theme}) => theme.colors.severity.warning};
    color: ${({theme}) => theme.colors.text.warning};
  }

  &[data-state='disabled'] {
    border-color: ${({theme}) => theme.colors.palettes.neutral[40][100]};
    background-color: ${({theme}) => theme.colors.palettes.neutral[20][100]};
    color: ${({theme}) => theme.colors.text.tertiary};
  }
`;

const Connector = styled.span`
  position: absolute;
  /* Sits above the hover surface so hovering a step never breaks the line. */
  z-index: 2;
  top: ${({theme}) => theme.getSize(5)};
  left: calc(50% + ${({theme}) => theme.getSize(3)});
  right: calc(-50% + ${({theme}) => theme.getSize(3)});
  height: 1px;
  background-color: ${({theme}) => theme.colors.general.separator};
  transition: background-color ${TRANSITION};

  /* The first step is half a slice wide with its bullet on the left edge, so its connector
     starts at that bullet and reaches a full item width to the next (centred) bullet. */
  &[data-first='true'] {
    left: calc(${({theme}) => theme.getSize(5)} + ${({theme}) => theme.getSize(3)});
    right: calc(-100% + ${({theme}) => theme.getSize(3)});
  }

  /* The last step is half a slice too, with its bullet on the right edge — the connector
     pointing at it stops one bullet width earlier than for a centred one. */
  &[data-before-last='true'] {
    right: calc(-50% + ${({theme}) => theme.getSize(8)});
  }

  &[data-first='true'][data-before-last='true'] {
    right: calc(-100% + ${({theme}) => theme.getSize(8)});
  }

  &[data-done='true'] {
    background-color: ${({theme}) => theme.colors.general.accent};
  }
`;

export function ConfiguratorStepper(props: ConfiguratorStepperProps) {
  return (
    <nav
      aria-label={
        props.label ??
        i18n.t('entity.newVehicle.labels.configuratorSteps', {
          defaultValue: 'Configurator steps',
        })
      }
      data-testid={props['data-testid']}
    >
      <List>
        {props.steps.map((step, index) => {
          const isCurrent = index === props.currentIndex;
          const isDone = step.isComplete ?? (index < props.currentIndex && !step.isDisabled);
          // Not limited to steps already behind the author: a step they filled in and broke is
          // worth raising wherever it sits. The step underfoot stays plain, since the footer
          // already spells out what is wrong with it.
          const needsAttention = step.isComplete === false && !isCurrent;
          const state = match({
            isCurrent,
            isDone,
            needsAttention,
            isDisabled: Boolean(step.isDisabled),
          })
            .with({isCurrent: true}, () => 'current' as const)
            .with({isDone: true}, () => 'done' as const)
            .with({needsAttention: true}, () => 'attention' as const)
            .with({isDisabled: true}, () => 'disabled' as const)
            .otherwise(() => 'upcoming' as const);
          const titleColor = match<typeof state, ThemeColorTextPath>(state)
            .with('current', () => 'primary')
            .with('attention', () => 'warning')
            .with('disabled', () => 'tertiary')
            .otherwise(() => 'secondary');
          const bulletTextColor = match(state)
            .with('current', () => 'white' as const)
            .with('attention', () => 'warning' as const)
            .otherwise(() => undefined);
          const incompleteLabel = i18n.t('entity.newVehicle.labels.stepIncomplete', {
            defaultValue: 'incomplete',
          });
          return (
            <Item key={step.title}>
              <StepButton
                type="button"
                title={step.title}
                disabled={step.isDisabled || !props.onStepChange}
                data-readonly={!props.onStepChange}
                data-align={match(index)
                  .with(0, () => 'start')
                  .with(props.steps.length - 1, () => 'end')
                  .otherwise(() => 'center')}
                aria-current={isCurrent ? 'step' : undefined}
                aria-label={needsAttention ? `${step.title} — ${incompleteLabel}` : step.title}
                onClick={() => props.onStepChange?.(index)}
                data-testid={suffixTestId(`step-[${index}]`, props)}
              >
                <Bullet data-state={state} aria-hidden>
                  {isDone ? (
                    <Icon value="navigation/check" size={4} color="general.white" />
                  ) : (
                    // A bare mark, like the check on a done step: the bullet is already the badge,
                    // so a warning icon would nest a second container inside it and outweigh the
                    // digits it sits beside.
                    <Text size="xSmall" alternative color={bulletTextColor}>
                      {state === 'attention' ? '!' : String(index + 1)}
                    </Text>
                  )}
                </Bullet>
                <StepTitle data-current={isCurrent}>
                  <Text size="small" alternative={isCurrent} color={titleColor}>
                    {step.title}
                  </Text>
                </StepTitle>
              </StepButton>
              <Show when={index < props.steps.length - 1}>
                <Connector
                  data-done={isDone}
                  data-first={index === 0}
                  data-before-last={index === props.steps.length - 2}
                  aria-hidden
                />
              </Show>
            </Item>
          );
        })}
      </List>
    </nav>
  );
}
